import { getEffectiveCableFuse, type CableSpec } from "./cable-current";
import type { DefLookup } from "./catalog";
import { PARALLAX_DC_BOARD_ID, parallaxDcBoardLimits } from "./catalog/parallax";
import type { Cable, SystemNode } from "./types";

/** NBS Handbook100, as used by wire-tables.ts; compare actual conductor area,
 * not the nearest display AWG attached to a metric wire. */
const awgArea = (gauge: number) => Number((Math.PI / 4 * (0.127 * 92 ** ((36 - gauge) / 39)) ** 2).toFixed(5));
export function fitsBoardWireRange(mm2: number, range: { smallest: number; largest: number }): boolean {
  return mm2 >= awgArea(range.smallest) - 1e-5 && mm2 <= awgArea(range.largest) + 1e-5;
}

export function fuseBoardWireMinimum(cable: Cable, nodes: Record<string, SystemNode>, lookup: DefLookup): number {
  let minimum = 0;
  for (const [nodeId, terminalId] of [[cable.fromNodeId, cable.fromTerminalId], [cable.toNodeId, cable.toTerminalId]]) {
    const node = nodes[nodeId];
    if (node?.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    const range = def && parallaxDcBoardLimits(def)?.wireAwgRange[terminalId];
    if (range) minimum = Math.max(minimum, awgArea(range.smallest));
  }
  return minimum;
}

/** A slot's published maximum fuse is not a current source, contact ampacity,
 * or permission to clamp a too-large load to the slot rating. */
export function applyFuseBoardCableLimits(cable: Cable, nodes: Record<string, SystemNode>, lookup: DefLookup, spec: CableSpec): CableSpec {
  if (spec.isAc) return spec;
  let maximum = Infinity;
  let identityUnverified = false;
  let wireFitUnverified = false;
  for (const [nodeId, terminalId] of [[cable.fromNodeId, cable.fromTerminalId], [cable.toNodeId, cable.toTerminalId]]) {
    const node = nodes[nodeId];
    if (node?.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (def?.id !== PARALLAX_DC_BOARD_ID) continue;
    const limits = parallaxDcBoardLimits(def);
    if (!limits) { identityUnverified = true; continue; }
    maximum = Math.min(maximum, limits.slotFuseMaxA[terminalId] ?? Infinity);
    const range = limits.wireAwgRange[terminalId];
    if (range && !fitsBoardWireRange(spec.wire.mm2, range)) wireFitUnverified = true;
  }
  // Manufacturer advice must not hide a larger existing declaration.
  const candidate = Math.max(spec.manufacturerFuseA ?? 0, spec.fuse);
  if (!identityUnverified && !wireFitUnverified && candidate <= maximum) return spec;
  return { ...spec,
    // Preserve a drawn value for reports/diagnostics; never turn it into fresh
    // advice. With no declaration, zero means no automatic recommendation.
    fuse: getEffectiveCableFuse(cable, nodes, lookup).ratingA != null ? spec.fuse : 0,
    manufacturerFuseA: undefined,
    ...(identityUnverified || wireFitUnverified ? { manufacturerInstallationUnverified: true as const } : {}),
    fuseRecommendationUnavailableReason: candidate > maximum ? "equipment-slot-limit" : "manufacturer-installation",
  };
}
